/**
 * Local CCP diagnostics shared by Settings and the softphone recovery panel.
 * Checks: (1) microphone getUserMedia
 *         (2) ICE/STUN against Amazon Connect TurnNlb hosts (same as endpoint-test telemetry).
 */

import {probeMediaEndpoint} from './connectEndpointTest/media'
import {
  DEFAULT_DIAGNOSTIC_REGIONS,
  getRegionForCode,
} from './connectEndpointTest/regions'

export type CcpDiagnosticCheckId = 'microphone' | 'ice'

export interface CcpDiagnosticCheckResult {
  id: CcpDiagnosticCheckId
  ok: boolean
  message: string
}

export interface CcpLocalDiagnosticsReport {
  ok: boolean
  checks: CcpDiagnosticCheckResult[]
  ranAt: number
}

async function checkMicrophone(): Promise<CcpDiagnosticCheckResult> {
  try {
    if (!navigator.mediaDevices?.getUserMedia) {
      return {
        id: 'microphone',
        ok: false,
        message: 'Microphone API unavailable in this browser',
      }
    }
    const stream = await navigator.mediaDevices.getUserMedia({audio: true})
    stream.getTracks().forEach((t) => t.stop())
    return {id: 'microphone', ok: true, message: 'Microphone check passed'}
  } catch {
    return {
      id: 'microphone',
      ok: false,
      message: 'Microphone check failed — allow mic access in the browser',
    }
  }
}

/**
 * Probe Connect softphone TurnNlb STUN (UDP 3478).
 * Pass if any regional host returns an srflx/relay candidate (same criteria as telemetry).
 */
async function checkIceStun(): Promise<CcpDiagnosticCheckResult> {
  const regionCode = DEFAULT_DIAGNOSTIC_REGIONS[0]
  const region = getRegionForCode(regionCode)
  if (!region?.mediaEndpoints.length) {
    return {
      id: 'ice',
      ok: false,
      message: 'Connect media STUN hosts not configured',
    }
  }

  try {
    const results = await Promise.all(
      region.mediaEndpoints.map((host) => probeMediaEndpoint(host)),
    )
    if (results.some((r) => r.success)) {
      return {id: 'ice', ok: true, message: 'Connect network ICE check passed'}
    }
    if (results.some((r) => r.timedOut)) {
      return {
        id: 'ice',
        ok: false,
        message: 'Connect ICE timed out — firewall may block UDP/STUN (3478)',
      }
    }
    return {id: 'ice', ok: false, message: 'Connect network ICE check failed'}
  } catch {
    return {id: 'ice', ok: false, message: 'Connect network ICE check failed'}
  }
}

export async function runCcpLocalDiagnostics(): Promise<CcpLocalDiagnosticsReport> {
  const mic = await checkMicrophone()
  const ice = mic.ok
    ? await checkIceStun()
    : {
        id: 'ice' as const,
        ok: false,
        message: 'Skipped — fix microphone access first',
      }

  return {
    ok: mic.ok && ice.ok,
    checks: [mic, ice],
    ranAt: Date.now(),
  }
}
